import { Eyebrow } from "@/components/Eyebrow";
import { Reveal } from "@/components/Reveal";
import { ScaleReveal } from "@/components/ScaleReveal";
import { Mark } from "@/components/Mark";

const PALANCAS = [
  "Trato personalizado",
  "Experiencia homogénea",
  "Compra recurrente",
  "Comunicación directa",
];

export default function Frutas() {
  return (
    <section data-testid="frutas-section">
      {/* UNIVERSO CLARO — RETO Y ESTRATEGIA */}
      <div className="px-6 pb-20 pt-28 md:px-12 md:pt-40 lg:px-20">
        <div className="mx-auto max-w-6xl">
          <Eyebrow index="07" label="Experiencia real · Retail · Fidelización" />
          <Reveal delay={0.1}>
            <h2 className="mt-10 font-serif text-4xl uppercase leading-[1.05] tracking-wide sm:text-5xl lg:text-6xl">
              Frutas Nieves
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl font-serif text-2xl italic leading-snug text-mirian-wine/85 sm:text-3xl lg:text-4xl">
              ¿Cómo conseguir que un cliente vuelva más sin depender del
              descuento?
            </p>
          </Reveal>

          <div className="mt-16 grid gap-14 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-5">
              <Reveal>
                <p className="text-xs font-medium uppercase tracking-[0.28em] text-mirian-wine/50">
                  El reto
                </p>
                <p className="mt-5 text-base leading-relaxed text-mirian-wine/75">
                  Incrementar la frecuencia de compra y la fidelidad de los
                  clientes habituales en 7 tiendas, aumentando la recurrencia y
                  el ticket medio sin modificar precios ni depender de
                  promociones agresivas.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <Reveal delay={0.1}>
                <p className="text-xs font-medium uppercase tracking-[0.28em] text-mirian-wine/50">
                  La estrategia
                </p>
                <p className="mt-5 text-base leading-relaxed text-mirian-wine/75">
                  Trabajamos sobre cuatro palancas:
                </p>
                <ul className="mt-6">
                  {PALANCAS.map((p, i) => (
                    <Reveal key={p} delay={0.15 + i * 0.12} y={14}>
                      <li
                        data-testid={`frutas-palanca-${i + 1}`}
                        className="border-t border-mirian-wine/15 py-4 text-sm font-medium uppercase tracking-[0.18em] text-mirian-wine"
                      >
                        {p}
                      </li>
                    </Reveal>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </div>

      {/* CÓMO FUNCIONÓ — contenido e imagen en la misma composición */}
      <div
        data-testid="frutas-como-funciono"
        className="grid items-center gap-10 md:grid-cols-12 md:gap-0"
      >
        <div className="px-6 py-16 md:col-span-7 md:py-24 md:pl-12 md:pr-14 lg:pl-20">
          <div className="max-w-xl md:ml-auto">
            <Reveal>
              <p className="text-xs font-medium uppercase tracking-[0.28em] text-mirian-wine/50">
                Cómo funcionó
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-10 font-serif text-xl uppercase tracking-wide sm:text-2xl">
                En tienda
              </p>
              <p className="mt-4 text-sm leading-relaxed text-mirian-wine/75 md:text-base">
                Personalizamos la atención y alineamos al equipo para ofrecer
                una experiencia coherente en las 7 tiendas, independientemente
                de quién atendiera al cliente.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-mirian-wine/75 md:text-base">
                También reforzamos productos capaces de generar hábitos de
                compra recurrentes.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-10 font-serif text-xl uppercase tracking-wide sm:text-2xl">
                Entre una compra y la siguiente
              </p>
              <p className="mt-4 text-sm leading-relaxed text-mirian-wine/75 md:text-base">
                Utilizamos WhatsApp para mantener la relación con el cliente
                mediante promociones del día, productos de temporada,
                recomendaciones semanales y acciones puntuales.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-10 text-sm text-mirian-wine/75 md:text-base">
                La idea no era enviar más mensajes.
              </p>
              <p
                data-testid="frutas-entre-compras"
                className="mt-3 font-serif text-xl italic leading-snug text-mirian-wine sm:text-2xl"
              >
                Era mantener la tienda presente entre una compra y la siguiente.
              </p>
            </Reveal>
          </div>
        </div>
        <div className="md:col-span-5">
          <ScaleReveal>
            <img
              data-testid="frutas-imagen"
              src="/images/frutas-nieves-naranja.png"
              alt="Una naranja partida entre un fondo crema y un fondo borgoña"
              loading="lazy"
              className="h-auto w-full"
            />
          </ScaleReveal>
        </div>
      </div>

      {/* UNIVERSO OSCURO — RESULTADOS */}
      <div className="bg-mirian-wine px-6 py-24 text-mirian-cream md:px-12 md:py-32 lg:px-20">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-mirian-cream/50">
              Resultados
            </p>
          </Reveal>
          <div className="mt-16 grid gap-20 md:grid-cols-2 md:gap-16">
            <Reveal delay={0.1}>
              <div data-testid="frutas-resultado-frecuencia">
                <p className="font-serif text-6xl leading-none sm:text-7xl lg:text-8xl">
                  2,8 <span className="text-mirian-cream/40">→</span> 3,4
                </p>
                <p className="mt-5 text-sm uppercase tracking-[0.2em] text-mirian-cream/60">
                  Visitas por cliente / mes
                </p>
                <p className="mt-8 font-serif text-4xl italic text-mirian-lime sm:text-5xl">
                  +21%
                </p>
                <p className="mt-2 text-xs uppercase tracking-[0.22em] text-mirian-cream/50">
                  Frecuencia de compra
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div data-testid="frutas-resultado-ticket">
                <p className="font-serif text-6xl leading-none sm:text-7xl lg:text-8xl">
                  18&nbsp;€ <span className="text-mirian-cream/40">→</span> 21&nbsp;€
                </p>
                <p className="mt-5 text-sm uppercase tracking-[0.2em] text-mirian-cream/60">
                  Ticket medio recurrente
                </p>
                <p className="mt-8 font-serif text-4xl italic text-mirian-lime sm:text-5xl">
                  +16,7%
                </p>
                <p className="mt-2 text-xs uppercase tracking-[0.22em] text-mirian-cream/50">
                  Ticket medio
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="mt-20 max-w-xl text-sm leading-relaxed text-mirian-cream/55">
              Mejora de la rotación de productos de consumo semanal y
              consolidación de WhatsApp como canal de recurrencia.
            </p>
          </Reveal>
        </div>
      </div>

      {/* CIERRE A DOS MITADES */}
      <div className="grid md:grid-cols-2">
        <div className="flex items-center justify-end bg-mirian-cream px-6 py-16 md:py-24 md:pl-12 md:pr-10 lg:pl-20">
          <Reveal>
            <p className="max-w-md font-serif text-2xl leading-snug text-mirian-wine sm:text-3xl">
              La fidelización no empezó intentando vender más.
            </p>
          </Reveal>
        </div>
        <div className="flex items-center bg-mirian-wine px-6 py-16 text-mirian-cream md:py-24 md:pl-10 md:pr-12 lg:pr-20">
          <Reveal delay={0.2}>
            <p className="max-w-md font-serif text-2xl leading-snug sm:text-3xl">
              Empezó dando al cliente{" "}
              <Mark underline delay={0.5}>
                más razones para volver
              </Mark>
              .
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
